// 블로그 섹션 / 포스트 데이터.
// ------------------------------------------------------------------
// 포스트는 더 이상 하드코드가 아니다. 빌드 직전에 scripts/fetch-blog-data.js 가
// 백엔드 공개 API 를 긁어 blogPosts.generated.json 으로 떨구고, 이 모듈이 그걸 읽는다.
//
// 동기 import 라서 vite-react-ssg 가 프리렌더할 때 본문이 HTML 에 그대로 박힌다.
// (브라우저에서 fetch 하면 크롤러가 빈 페이지를 보고, sitemap 에도 안 들어간다.)
//
// 섹션 메타(label/desc/group)는 편집 문구라 여기 남긴다. count 만 실제 글 수로 채운다.
// 페이지들이 쓰던 헬퍼 시그니처는 그대로 유지했다.
// ------------------------------------------------------------------

import generated from './blogPosts.generated.json';

/** @typedef {{ name: string, role: string, photo: string|null, initials: string }} BlogAuthor */
/**
 * @typedef {Object} BlogPost
 * @property {number} id
 * @property {string} slug
 * @property {string} section        섹션 슬러그 (예: 'medical-cases')
 * @property {string} title
 * @property {string} summary
 * @property {string} content        본문 HTML
 * @property {BlogAuthor} author
 * @property {string} publishedAt    '2026.04.18'
 * @property {number} readMinutes
 * @property {string|null} thumbnailUrl
 * @property {string} metaTitle
 * @property {string} metaDescription
 * @property {string[]} tags
 * @property {boolean} featured
 */

/** @type {BlogPost[]} */
export const blogPosts = generated.posts;

/** 스냅샷 생성 시각. 발행 후 재빌드가 안 됐는지 확인할 때 쓴다. */
export const blogGeneratedAt = generated.generatedAt;

const SECTION_META = [
  {
    slug: 'medical-cases',
    group: '업무 사례',
    label: '의료사고',
    desc: '의료사고로 인한 분쟁의 사실관계와 법적 쟁점을 정리한 사례.',
  },
  {
    slug: 'disputes',
    group: '업무 사례',
    label: '분쟁사례',
    desc: '의료기관·제약사·환자 간 발생한 분쟁의 합의·중재·소송 진행 사례.',
  },
  {
    slug: 'damages',
    group: '업무 사례',
    label: '손해배상',
    desc: '치료비·일실수입·위자료 등 손해배상 산정 실제 판례.',
  },
  {
    slug: 'news',
    group: '메디로이어',
    label: '메디로이어 소식',
    desc: '플랫폼·로펌의 공식 발표와 미디어 노출.',
  },
  {
    slug: 'newsletter',
    group: '메디로이어',
    label: '뉴스레터',
    desc: '월간 큐레이션 — 의료법률 트렌드와 판례 요약.',
  },
  {
    slug: 'opinion',
    group: '메디로이어',
    label: '오피니언',
    desc: '검증된 변호사·의료진의 시론과 칼럼.',
  },
];

/** 섹션 메타 + 실제 발행 글 수. */
export const blogSections = SECTION_META.map((meta) => ({
  ...meta,
  count: blogPosts.filter((p) => p.section === meta.slug).length,
}));

// 헬퍼들 — 페이지에서 import해 사용

export function getBlogSection(slug) {
  return blogSections.find((s) => s.slug === slug);
}

export function getBlogPostsBySection(slug) {
  if (!slug || slug === 'all') return blogPosts;
  return blogPosts.filter((p) => p.section === slug);
}

export function getBlogPostBySlug(slug) {
  return blogPosts.find((p) => p.slug === slug);
}

export function getSectionLabel(slug) {
  return getBlogSection(slug)?.label ?? slug;
}

export function searchPosts(posts, query) {
  if (!query || !query.trim()) return posts;
  const q = query.trim().toLowerCase();
  return posts.filter(
    (p) =>
      p.title.toLowerCase().includes(q) ||
      p.summary.toLowerCase().includes(q) ||
      p.author.name.toLowerCase().includes(q) ||
      p.tags.some((tag) => tag.toLowerCase().includes(q))
  );
}

export function sortPosts(posts, sort = 'recent') {
  const copy = [...posts];
  if (sort === 'popular') {
    copy.sort((a, b) => b.readMinutes - a.readMinutes);
  } else {
    copy.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }
  return copy;
}
